import { ref, computed, reactive } from 'vue'
import { defineStore } from 'pinia'
import { DAY_TYPE, PERIOD_STATUS, type Employee, type DailyTime, type Payslip, type Period, type Schedule } from './types'
import { faker } from '@faker-js/faker'
import { useEmployeeStore } from './employee'
import { usePayslipStore } from './payslip'
import { useAppStore } from './app'
import { useTableStore } from './table'
import { usePeriodStore } from './period'
import { useScheduleStore } from './schedule'

import dayjs from 'dayjs'
import axios from 'axios'
import { useUtil } from '@/composables/util'

const LOG_FORMAT_STR = 'YYYY-MM-DD HH:mm:ss'
const my = useUtil()


const apiURL: string = import.meta.env.VITE_API_URL + '/api'
// const apiURL = "http://localhost:4000/api"

const api = axios.create({
  baseURL: apiURL + '/v1'
})

export const useDailyTimeStore = defineStore('dailytime', () => {
  const DayTypes = Object.values({ ...DAY_TYPE })
  const dailyTimes = reactive<DailyTime[]>([])
  const selectedDailyTime = ref({} as DailyTime) // selected in table
  const thisDailyTime = ref({} as DailyTime) //for form editing

  const timelogs = ref({} as any)
  const thisLog = ref<any>(dayjs())

  const showForm = ref(false)

  const periodStore = usePeriodStore()
  const employeeStore = useEmployeeStore()
  const payslipStore = usePayslipStore()
  const scheduleStore = useScheduleStore()

  const appStore = useAppStore()
  const tableStore = useTableStore()

  async function generateDailyTimes(count = 200) {
    if (dailyTimes.length > 0) {
      return
    }

    const year = dayjs().get('year')
    const month = dayjs().get('month')

    const start_date = dayjs().startOf('month')
    const end_date = dayjs(start_date).add(15, 'day')

    const hour_in = 8
    const hour_out = 17

    payslipStore.payslips.forEach((payslip: Payslip) => {
      let day_date = start_date
      while (day_date < end_date) {
        const day = dayjs(day_date).get('date')

        const dailytime = {} as DailyTime

        dailytime.day_type = DAY_TYPE.WorkingDay
        dailytime.payslip_uuid = payslip.uuid

        const emp = employeeStore.employees.find(
          (employee: Employee) => employee.uuid == payslip.employee_uuid
        )
        dailytime.employee_uuid = emp?.uuid
        dailytime.employee_id = emp?.employee_id

        dailytime.time_in = dayjs()
          .set('year', year)
          .set('month', month)
          .set('date', day)
          .set('hour', hour_in)
          .set('minute', 0)
          .set('second', 0)
          .toDate()

        dailytime.time_out = dayjs()
          .set('year', year)
          .set('month', month)
          .set('date', day)
          .set('hour', hour_out)
          .set('minute', 0)
          .set('second', 0)
          .toDate()
        addDailyTime(dailytime)
        day_date = dayjs(day_date).add(1, 'day')
      }
    })
  }

  // recalc immediately when the payslip belongs to the selected Open period
  // (App.vue keeps that period's daily times and transactions loaded)
  function recalcPayslipNow(payslip_uuid?: string) {
    const payslip = payslipStore.payslips.find((item: Payslip) => item?.uuid == payslip_uuid)
    if (payslip != undefined && appStore.thisPeriod?.status == PERIOD_STATUS.Open) {
      payslipStore.calcPay(payslip)
    }
  }

  async function addDailyTime(payload: DailyTime) {
    const response = await api.post('/dailytimes.json', payload)

    if (response.status == 200) {
      if ('errors' in Object.keys(response.data)) {
        appStore.errors = response.data.errors
      } else {
        dailyTimes.push(response.data)
        payslipStore.dirtyPayslip(payload.payslip_uuid as string)
      }
    } else {
    }
  }

  async function saveDailyTime(payload: DailyTime) {
    if (payload.uuid) {
      const response = await api.put('/dailytimes/' + payload.uuid, payload)

      if (response.status == 200) {
        const idx = dailyTimes.findIndex((item: any) => item.uuid == payload.uuid)
        // the v1 PUT returns the row flat — response.data.data is undefined
        // and used to replace the edited row with undefined, crashing the
        // next recalc's day loop
        dailyTimes.splice(idx, 1, response.data?.data ?? response.data)
      } else {
      }
    } else {
      await addDailyTime(payload)
    }
    await payslipStore.dirtyPayslip(payload.payslip_uuid as string)
    recalcPayslipNow(payload.payslip_uuid)
  }

  // Repoint a payslip's daily-time rows to a schedule (e.g. the employee's
  // ShiftCode changed), keeping logs intact. Persists each changed row; the
  // caller recalcs once afterwards. Returns the count of rows changed.
  async function setPayslipSchedule(payslip_uuid: string, schedule_uuid?: string) {
    const rows = dailyTimes.filter(
      (dt) => dt?.payslip_uuid == payslip_uuid && dt.schedule_uuid != schedule_uuid
    )
    for (const dt of rows) {
      const response = await api.put('/dailytimes/' + dt.uuid, { ...dt, schedule_uuid })
      if (response.status == 200) {
        const idx = dailyTimes.findIndex((item) => item?.uuid == dt.uuid)
        dailyTimes.splice(idx, 1, response.data?.data ?? response.data)
      }
    }
    return rows.length
  }

  const selectedPayslipDailyTimes = computed(() => {
    const ds = dailyTimes.filter(item => item != undefined).filter((item: DailyTime) => item.payslip_uuid == payslipStore.thisPayslip.uuid)
    return ds
  })

  // recalc=false lets bulk callers (generatePayslipDailyTimes) recalc once at the end
  async function deleteDailyTime(payload: DailyTime, recalc: boolean = true) {
    const response = await api.delete('/dailytimes/' + payload.uuid)
    // record was actually deleted
    if (response.status == 200) {
      const ndx = dailyTimes.findIndex(
        (item: DailyTime) => item.uuid == payload.uuid
      );
      dailyTimes.splice(ndx, 1)
      if (recalc) {
        await payslipStore.dirtyPayslip(payload.payslip_uuid as string)
        recalcPayslipNow(payload.payslip_uuid)
      }
    }
  }

  const selectedPeriodDailyTimes = computed(() => {
    const ds = dailyTimes.filter((item: DailyTime) => item.period_uuid == appStore.thisPeriod.uuid)
    return ds
  })

  const hoursTally = computed(() => (dailytime: DailyTime) => {
    const time_in = dayjs(dailytime.time_in)
    const time_out = dayjs(dailytime.time_out)
    const hours = time_out.diff(time_in, 'hour')
    return hours
  })

  const hours = computed(() => (dailytime: DailyTime) => {
    const time_in = dayjs(dailytime.time_in)
    const time_break = dayjs(dailytime.time_break)
    const time_resume = dayjs(dailytime.time_resume)
    const time_out = dayjs(dailytime.time_out)

    const hours =
      (time_out.diff(time_in, 'minute') - (time_resume.diff(time_break, 'minute') || 60)) / 60
    return hours
  })

  const undertime = computed(() => (dailytime: DailyTime) => {
    const time_in = dayjs(dailytime.time_in)
    const time_break = dayjs(dailytime.time_break)
    const time_resume = dayjs(dailytime.time_resume)
    const time_out = dayjs(dailytime.time_out)
    const hours =
      (time_out.diff(time_in, 'minute') - (time_resume.diff(time_break, 'minute') || 60)) / 60

    const undertime = hours < 8 ? 8 - hours : 0

    return undertime
  })

  const overtime = computed(() => (dailytime: DailyTime) => {
    const time_in = dayjs(dailytime.time_in)
    const time_break = dayjs(dailytime.time_break)
    const time_resume = dayjs(dailytime.time_resume)
    const time_out = dayjs(dailytime.time_out)
    const hours =
      (time_out.diff(time_in, 'minute') - (time_resume.diff(time_break, 'minute') || 60)) / 60

    const overtime = hours > 8 ? hours - 8 : 0

    return overtime
  })

  const importDailyTime = async (payload: any) => {
    // temp override year and month
    const thisMonth = dayjs(appStore.thisPeriod.start_date).month()
    const thisYear = dayjs(appStore.thisPeriod.start_date).year()

    // generate timelogs
    timelogs.value = {}

    for (let index = 0; index < payload.length; index++) {
      const item = payload[index]

      // item.DateTime = dayjs(item.DateTime).set('year', thisYear).set('month',thisMonth).format('YYYY-MM-DD hh:mm:ss')
      item.DateTime = dayjs(item.DateTime, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD H:mm:ss']).format('YYYY-MM-DD HH:mm:ss')


      const id = item.Id
      const dkey = dayjs(item.DateTime).format('YYYYMMDD')

      // get the Schedule 
      const employee = employeeStore.employees.find(
        (item: Employee) => Number.parseInt(item.employee_id) == Number.parseInt(id)
      )

      if (!employee) {
        continue
      }

      const payslip = payslipStore.payslips.find(
        (item: Payslip) =>
          item.period_uuid == appStore.thisPeriod.uuid && item.employee_uuid == employee?.uuid
      )

      if (!payslip) {
        continue
      }

      const schedule = scheduleStore.schedules.find(
        (item: Schedule) =>
          item.uuid == payslip.schedule_uuid
      )
      if (!schedule) {
        continue
      }

      // sanitize Shift
      const shift = my.sanitizeShift({
        time_in: schedule.time_in, time_break: schedule.time_break,
        time_resume: schedule.time_resume, time_out: schedule.time_out
      },true)

      if (dayjs(item.DateTime) < dayjs(appStore.thisPeriod.start_date).set('hour', 0).set('minute', 0).set('second', 0)) {
        continue
      }

      // add one day to accommodate night shifts which ends next day
      if (dayjs(item.DateTime) > dayjs(appStore.thisPeriod.end_date).add(1, 'day').set('hour', 23).set('minute', 59).set('second', 59)) {
        break
      }


      if (!Object.keys(timelogs.value).includes(id)) {
        timelogs.value[id] = {}
      }

      if (!Object.keys(timelogs.value[id]).includes(dkey)) {
        timelogs.value[id][dkey] = { logs: '' }
      }

      // verify for night shift (next days) ----> THIS COULD BE ERRONEOUS
      if (
        ((shift as any).next_days[2] >= 1) && false &&
        dayjs(item.DateTime).hour() < (shift as any).time_in.subtract(2, 'hour').hour()
      ) {
        // these are next day swipes, dkey should be previous day
        const dkey_ = dayjs(item.DateTime).add(-1, 'day').format('YYYYMMDD')

        if (!Object.keys(timelogs.value).includes(id)) {
          timelogs.value[id] = {}
        }

        if (!Object.keys(timelogs.value[id]).includes(dkey_)) {
          timelogs.value[id][dkey_] = { logs: '' }
        }

        const l = timelogs.value[id][dkey_]['logs'] + item.DateTime + ', '
        timelogs.value[id][dkey_]['logs'] = l
      } else {
        // not night shift next days so exclude past period end date
        if (dayjs(item.DateTime) > dayjs(appStore.thisPeriod.end_date).set('hour', 23).set('minute', 59).set('second', 59)) {
          continue
        }

        const l = timelogs.value[id][dkey]['logs'] + item.DateTime + ', '
        timelogs.value[id][dkey]['logs'] = l
      }
    }

    // generate dailytime
    Object.keys(timelogs.value).forEach((id) => {
      Object.keys(timelogs.value[id]).forEach((date) => {
        let myid = 0
        const payload = {} as DailyTime
        payload.uuid = faker.string.uuid()
        // period_uuid + payslip_uuid
        payload.employee_id = id

        const employee = employeeStore.employees.find(
          (item: Employee) => Number.parseInt(item.employee_id) == Number.parseInt(id)
        )
        const payslip = payslipStore.payslips.find(
          (item: Payslip) =>
            item.period_uuid == appStore.thisPeriod.uuid && item.employee_uuid == employee?.uuid
        )

        const schedule = scheduleStore.schedules.find(
          (item: Schedule) =>
            item.uuid == payslip?.schedule_uuid
        )

        payload.period_uuid = payslip?.period_uuid
        payload.payslip_uuid = payslip?.uuid
        payload.employee_uuid = employee?.uuid
        payload.schedule_uuid = schedule?.uuid
        payload.company_uuid = appStore.thisCompany.uuid

        payload.date_ = date
        payload.logs = timelogs.value[id][date]['logs']

        // check if Holiday
        const md = dayjs(date, 'YYYYMMDD').format('M/D')
        const idx = tableStore.HOLIDAY.findIndex((item: any) => item.Day == md)
        if (idx >= 0) {
          payload.day_type = tableStore.HOLIDAY[idx].Type
        } else {
          payload.day_type = DAY_TYPE.WorkingDay
        }
        if (myid < 1) {
          addDailyTime(payload)
          myid += 1
        }

      })
    })



  }

  async function getPeriodDailyTimes(payload: Period) {
    const response = await api.get('/dailytimes/period_uuid/' + payload.uuid)

    if (response.status == 200) {
      dailyTimes.length = 0
      response.data.data.forEach((item: DailyTime) => {
        dailyTimes.push(item)
      })
    } else {
    }
  }

  async function generatePayslipDailyTimes(payload: Payslip) {
    // const year = dayjs().get('year')
    // const month = dayjs().get('month')

    // STEP #1: remove payslip dailytimes
    dailyTimes.filter((item: DailyTime) => item.payslip_uuid == payload.uuid).forEach((item: DailyTime) => {
      deleteDailyTime(item, false)
    })

    // STEP #2: create payslip dailytimes
    const start_date = dayjs(appStore.thisPeriod.start_date).toDate()
    const end_date = dayjs(appStore.thisPeriod.end_date).toDate()


    const schedule = scheduleStore.schedules.find(
      (item: Schedule) => item.uuid == payload.schedule_uuid
    )

    // let hour_in = dayjs.utc(schedule.time_in).utcOffset(8).get('hour')
    const hour_in = dayjs.utc(schedule?.time_in).get('hour')
    // let min_in = dayjs.utc(schedule.time_in).utcOffset(8).get('minute')
    const min_in = dayjs.utc(schedule?.time_in).get('minute')

    // let hour_out = dayjs.utc(schedule.time_out).utcOffset(8).get('hour')
    const hour_out = dayjs.utc(schedule?.time_out).get('hour')
    // let min_out = dayjs.utc(schedule.time_out).utcOffset(8).get('minute')
    const min_out = dayjs.utc(schedule?.time_out).get('minute')

    // only generate logs for the schedule's Work Days (e.g. "M,T,W,Th,F");
    // an empty work_days means every day (schedules without work days set)
    const DAY_CODES = ['Su', 'M', 'T', 'W', 'Th', 'F', 'S']
    const work_days = (schedule?.work_days ?? '')
      .split(',')
      .map((d: string) => d.trim())
      .filter((d: string) => d != '')

    let day_date: any = start_date

    while (day_date <= end_date) {
      if (work_days.length > 0 && !work_days.includes(DAY_CODES[dayjs(day_date).day()])) {
        day_date = dayjs(day_date).add(1, 'day')
        continue
      }

      const day = dayjs(day_date).get('date')
      const year = dayjs(day_date).year()
      const month = dayjs(day_date).month()

      const dailytime = {} as DailyTime

      dailytime.day_type = DAY_TYPE.WorkingDay
      dailytime.date_ = dayjs(day_date).format('YYYYMMDD')

      dailytime.payslip_uuid = payload.uuid

      // const employee = employeeStore.employees.find((employee) => employee.uuid == payload.employee_uuid)

      dailytime.period_uuid = payload.period_uuid
      dailytime.employee_uuid = payload.employee_uuid
      dailytime.employee_id = payload.employee_id
      dailytime.schedule_uuid = payload.schedule_uuid
      dailytime.cost_uuid = payload.cost_uuid

      const sdate = dayjs().set('year', year)
        .set('month', month)
        .set('date', day)
        .set('hour', hour_in)
        .set('minute', min_in)
        .set('second', 0)

      let edate = sdate
      if (hour_out < hour_in) {
        edate = sdate.add(1, 'day').set('hour', hour_out)
      } else {
        edate = sdate.set('hour', hour_out)
      }

      dailytime.logs = [sdate.format('YYYY-MM-DD HH:mm:ss'), edate.format('YYYY-MM-DD HH:mm:ss')].join(',')


      await addDailyTime(dailytime)
      day_date = dayjs(day_date).add(1, 'day')
    }
  }

  const hasMissingFields = computed(() => (dailytime: any) => {
    if (!dailytime['schedule_uuid']
      || !dailytime['employee_uuid']
      || !dailytime['employee_id']
      || !dailytime['period_uuid']
      || (!dailytime['company_uuid'] && appStore.options.companyEnabled)
      || (!dailytime['cost_uuid'] && appStore.options.costEnabled)
      || !dailytime['payslip_uuid']
    ) {
      return true
    } else {
      return false
    }
  })

  return {
    showForm,
    dailyTimes,
    selectedDailyTime,
    thisDailyTime,
    selectedPayslipDailyTimes,
    hoursTally,
    hours,
    undertime,
    overtime,
    DayTypes,
    timelogs,
    thisLog,
    selectedPeriodDailyTimes,
    generateDailyTimes,
    generatePayslipDailyTimes,
    setPayslipSchedule,
    saveDailyTime,
    addDailyTime,
    deleteDailyTime,
    importDailyTime,
    getPeriodDailyTimes,

    hasMissingFields
  }
})
